import axios from 'axios';
import { getErrorMessage } from './errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 60000, // 60 second timeout for AI-powered features
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle errors with user-friendly messages
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle auth errors
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Only redirect if not already on login/register page
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
        window.location.href = '/login';
      }
    }
    
    // Attach user-friendly message to error
    error.userMessage = getErrorMessage(error);
    
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  deleteAccount: (password, confirmation) => api.delete('/auth/delete-account', { data: { password, confirmation } }),
};

// CV APIs
export const cvAPI = {
  create: (data) => api.post('/cv/create', data),
  list: () => api.get('/cv/list'),
  get: (id) => api.get(`/cv/${id}`),
  update: (id, data) => api.put(`/cv/${id}`, data),
  delete: (id) => api.delete(`/cv/${id}`),
  download: (id, template = null) => api.get(`/cv/${id}/download${template ? `?template=${template}` : ''}`, { responseType: 'blob' }),
  getTemplates: () => api.get('/cv/templates'),
  generateAchievement: (data) => api.post('/cv/achievement-generator', data),
  matchKeywords: (data) => api.post('/cv/keyword-matcher', data),
  getScore: (cvId) => api.post('/cv/score', { cv_id: cvId }),
};

// AI APIs
export const aiAPI = {
  enhance: (text, context) => api.post('/ai/enhance', { text, context }),
  translateExperience: (text) => api.post('/ai/translate-experience', { text, context: 'informal' }),
  matchJob: (cvId, jobDescription) => api.post('/ai/job-match', { cv_id: cvId, job_description: jobDescription }),
};

// Interview APIs
export const interviewAPI = {
  generateQuestions: (cvId) => api.post(`/interview/generate-questions?cv_id=${cvId}`),
};

// Subscription APIs
export const subscriptionAPI = {
  // PayFast endpoints (primary payment method)
  createPayfastCheckout: (useDiscount = false) => api.post('/payfast/create-checkout', { use_discount: useDiscount }),
  verifyPayfastPayment: (paymentId) => api.post('/payfast/verify-payment', { payment_id: paymentId }),
  cancel: () => api.post('/subscription/cancel'),
};

// Job Tracker APIs
export const jobsAPI = {
  add: (job) => api.post('/jobs/add', job),
  list: () => api.get('/jobs/list'),
  update: (id, status) => api.put(`/jobs/${id}?status=${status}`),
  delete: (id) => api.delete(`/jobs/${id}`),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Document Generator APIs
export const documentAPI = {
  generate: (data) => api.post('/documents/generate', data),
};

// WhatsApp APIs
export const whatsappAPI = {
  generateMessage: (data) => api.post('/whatsapp/generate-message', data),
};

// Job Board APIs
export const jobBoardAPI = {
  search: (data) => api.post('/jobs/search', data),
  analyzeUrl: (jobUrl, cvId) => api.post(`/jobs/analyze-url?job_url=${encodeURIComponent(jobUrl)}&cv_id=${cvId}`),
};

// Success Stories APIs
export const storiesAPI = {
  getAll: () => api.get('/success-stories'),
};

// Community Success Board APIs
export const communityAPI = {
  getFeed: (limit = 20, province = null) => api.get(`/community/feed?limit=${limit}${province ? `&province=${province}` : ''}`),
  postSuccess: (data) => api.post('/community/success', data),
  likePost: (postId) => api.post(`/community/like/${postId}`),
};

// Skills Gap Analyzer APIs
export const skillsAPI = {
  getJobs: () => api.get('/skills/jobs'),
  analyze: (data) => api.post('/skills/analyze', data),
  getCourses: (skill) => api.get(`/skills/courses/${encodeURIComponent(skill)}`),
};

// Mock Interview APIs
export const mockInterviewAPI = {
  start: (data) => api.post('/interview/mock/start', data),
  getFeedback: (data) => api.post('/interview/mock/feedback', data),
};

// Follow-Up APIs
export const followUpAPI = {
  getPending: () => api.get('/followup/pending'),
  markFollowedUp: (applicationId) => api.post(`/followup/mark/${applicationId}`),
};

// Salary Negotiation APIs
export const salaryAPI = {
  calculate: (data) => api.post('/salary/calculate', data),
};

// Guides APIs
export const guidesAPI = {
  getFirst90Days: () => api.get('/guide/first-90-days'),
  getWorkplaceRights: () => api.get('/guide/workplace-rights'),
};

// Email Templates APIs
export const templatesAPI = {
  getEmail: (data) => api.post('/templates/email', data),
};

// Career Path APIs
export const careerAPI = {
  getPaths: () => api.get('/career/paths'),
  getPlan: (data) => api.post('/career/plan', data),
};

// Gamification APIs
export const gamificationAPI = {
  getProgress: () => api.get('/gamification/progress'),
};

// Cover Letter Generator APIs
export const coverLetterAPI = {
  generate: (data) => api.post('/cover-letter/generate', data),
  downloadPdf: (data) => api.post('/cover-letter/download-pdf', data, { responseType: 'blob' }),
};

// Job Pipeline APIs (Enhanced Job Tracker)
export const pipelineAPI = {
  create: (job) => api.post('/pipeline/jobs', job),
  list: () => api.get('/pipeline/jobs'),
  get: (id) => api.get(`/pipeline/jobs/${id}`),
  update: (id, job) => api.put(`/pipeline/jobs/${id}`, job),
  delete: (id) => api.delete(`/pipeline/jobs/${id}`),
  updateStatus: (id, status) => api.put(`/pipeline/jobs/${id}/status?status=${status}`),
  addContact: (jobId, contact) => api.post(`/pipeline/jobs/${jobId}/contact`, contact),
  getFollowups: () => api.get('/pipeline/followups'),
  getStats: () => api.get('/pipeline/stats'),
};

// Job Match Analysis APIs
export const jobMatchAPI = {
  analyze: (data) => api.post('/job-match/analyze', data),
  quickScore: (data) => api.post('/job-match/quick-score', data),
};

// SA-Specific Feature APIs
export const saFeaturesAPI = {
  // Z83 Government Jobs
  getZ83Guide: () => api.get('/z83/guide'),
  exportZ83: (cvId) => api.post('/z83/export', { cv_id: cvId }),
  
  // Learnerships
  getLearnerships: (params) => api.get('/learnerships', { params }),
  getLearnershipDetail: (id) => api.get(`/learnerships/${id}`),
  
  // Job Boards
  getJobBoards: () => api.get('/job-boards'),
  shareToBoard: (cvId, boardId) => api.post('/job-boards/share', { cv_id: cvId, board_id: boardId }),
  
  // SETA Tracker
  getNqfLevels: () => api.get('/nqf-levels'),
  getSetas: () => api.get('/setas'),
  getSETACerts: (cvId) => api.get(`/cv/${cvId}/seta-certifications`),
  updateSETACerts: (cvId, certs) => api.put(`/cv/${cvId}/seta-certifications`, { cv_id: cvId, seta_certifications: certs }),
};

// Supporting Documents APIs
export const supportingDocsAPI = {
  upload: (formData) => api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  list: () => api.get('/documents/list'),
  get: (docId) => api.get(`/documents/${docId}`),
  download: (docId) => api.get(`/documents/${docId}/download`, { responseType: 'blob' }),
  delete: (docId) => api.delete(`/documents/${docId}`),
  mergeWithCV: (data) => api.post('/documents/merge-with-cv', data, { responseType: 'blob' }),
  getTypes: () => api.get('/documents/types'),
};

export default api;
